import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Eye, Check, X, Loader2, Mail, Phone, RefreshCw, CreditCard, Edit3, Trash2, Archive } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [authorizeRefund, setAuthorizeRefund] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ preferred_date: '', time_slot: '', confirmed_price: '' });
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null); // 'delete' | 'archive' | null

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);
  
  // Clear selection when filter changes
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/admin/bookings${params}`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (booking) => {
    setSelectedBooking(booking);
    setAdminNotes(booking.admin_notes || '');
    setAuthorizeRefund(null);  // Reset to force selection
    setEditMode(false);
    setEditData({ 
      preferred_date: booking.preferred_date || '', 
      time_slot: booking.time_slot || '',
      confirmed_price: booking.confirmed_price || ''
    });
    setDialogOpen(true);
  };

  const saveBookingChanges = async () => {
    if (!selectedBooking) return;
    setUpdating(true);
    try {
      const updateData = {
        preferred_date: editData.preferred_date || selectedBooking.preferred_date,
        time_slot: editData.time_slot || selectedBooking.time_slot,
        admin_notes: adminNotes
      };
      
      // Include confirmed_price if provided
      if (editData.confirmed_price) {
        updateData.confirmed_price = parseFloat(editData.confirmed_price);
      }
      
      await api.put(`/admin/bookings/${selectedBooking.id}`, updateData);
      toast.success('Prenotazione aggiornata');
      setEditMode(false);
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdating(false);
    }
  };

  const confirmPrice = async () => {
    if (!selectedBooking || !editData.confirmed_price) {
      toast.error('Inserisci il prezzo confermato');
      return;
    }
    setUpdating(true);
    try {
      await api.put(`/admin/bookings/${selectedBooking.id}/confirm-price`, {
        confirmed_price: parseFloat(editData.confirmed_price)
      });
      toast.success('Prezzo confermato e richiesta accettata! Il cliente può ora pagare la caparra.');
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Confirm price error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella conferma prezzo');
    } finally {
      setUpdating(false);
    }
  };

  // NEW: Save price only (Step 1)
  const savePrice = async () => {
    if (!selectedBooking || !editData.confirmed_price) {
      toast.error('Inserisci il prezzo definitivo');
      return;
    }
    setUpdating(true);
    try {
      const response = await api.put(`/admin/bookings/${selectedBooking.id}/set-price`, {
        confirmed_price: parseFloat(editData.confirmed_price)
      });
      toast.success('Prezzo salvato');
      setSelectedBooking(response.data);
      fetchBookings();
    } catch (error) {
      console.error('Save price error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio prezzo');
    } finally {
      setUpdating(false);
    }
  };

  // NEW: Accept booking (Step 2)
  const acceptBooking = async () => {
    if (!selectedBooking) return;
    if (!selectedBooking.confirmed_price || selectedBooking.confirmed_price <= 0) {
      toast.error('Devi prima impostare il prezzo definitivo');
      return;
    }
    setUpdating(true);
    try {
      const response = await api.put(`/admin/bookings/${selectedBooking.id}/accept`);
      toast.success('Richiesta accettata! Il cliente può ora pagare la caparra.');
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Accept booking error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'accettazione');
    } finally {
      setUpdating(false);
    }
  };

  // Bulk Actions
  const filteredBookings = bookings.filter(b => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return b.booking_code?.toLowerCase().includes(q) ||
           b.first_name?.toLowerCase().includes(q) ||
           b.last_name?.toLowerCase().includes(q) ||
           b.email?.toLowerCase().includes(q);
  });

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredBookings.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredBookings.map(b => b.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const bulkDelete = async () => {
    setBulkLoading(true);
    try {
      await api.post('/admin/bookings/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} prenotazioni eliminate`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchBookings();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Errore nell\'eliminazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkArchive = async () => {
    setBulkLoading(true);
    try {
      await api.post('/admin/bookings/bulk-archive', { ids: selectedIds });
      toast.success(`${selectedIds.length} prenotazioni archiviate`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchBookings();
    } catch (error) {
      console.error('Bulk archive error:', error);
      toast.error('Errore nell\'archiviazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa prenotazione?')) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success('Prenotazione eliminata');
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const archiveBooking = async (id) => {
    try {
      await api.post('/admin/bookings/bulk-archive', { ids: [id] });
      toast.success('Prenotazione archiviata');
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Archive error:', error);
      toast.error('Errore nell\'archiviazione');
    }
  };

  const updateStatus = async (status) => {
    if (!selectedBooking) return;

    setUpdating(true);
    try {
      await api.put(`/admin/bookings/${selectedBooking.id}/status?status=${status}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`);
      toast.success(`Prenotazione ${status === 'confermata' ? 'confermata' : status === 'rifiutata' ? 'rifiutata' : 'aggiornata'}`);
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdating(false);
    }
  };

  const approveCancellation = async (withRefund) => {
    if (!selectedBooking) return;

    setUpdating(true);
    try {
      const response = await api.put(`/admin/bookings/${selectedBooking.id}/approve-cancellation`, {
        authorize_refund: withRefund === true  // Explicit boolean
      });
      
      // Use response data for correct messaging
      const { refund_decision, refund_status } = response.data;
      let refundMsg = '';
      
      if (selectedBooking.payment_status === 'caparra_pagata') {
        if (refund_decision === 'refund') {
          if (refund_status === 'succeeded') {
            refundMsg = ' - Caparra rimborsata';
          } else if (refund_status === 'pending') {
            refundMsg = ' - Rimborso in corso';
          } else if (refund_status === 'failed') {
            refundMsg = ' - Rimborso fallito (verificare su Stripe)';
          }
        } else if (refund_decision === 'keep') {
          refundMsg = ' - Caparra trattenuta';
        }
      }
      
      toast.success('Cancellazione approvata' + refundMsg);
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    } finally {
      setUpdating(false);
    }
  };

  const approveModification = async () => {
    if (!selectedBooking) return;

    setUpdating(true);
    try {
      await api.put(`/admin/bookings/${selectedBooking.id}/approve-modification`, {});
      toast.success('Modifica approvata');
      setDialogOpen(false);
      fetchBookings();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Errore nell\'approvazione');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      in_attesa: 'bg-yellow-100 text-yellow-700',
      accettata: 'bg-green-200 text-green-800',
      rifiutata: 'bg-red-100 text-red-700',
      completata: 'bg-blue-100 text-blue-700',
      annullata: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      in_attesa: 'In Attesa',
      accettata: 'Accettata',
      rifiutata: 'Rifiutata',
      completata: 'Completata',
      annullata: 'Annullata'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${variants[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    if (paymentStatus === 'caparra_pagata') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-emerald-100 text-emerald-700">Caparra Pagata</span>;
    }
    if (paymentStatus === 'rimborsato') {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-700">Rimborsato</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-bookings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl mb-2">Prenotazioni</h1>
        <p className="text-muted-foreground">Gestisci le richieste di prenotazione</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per nome, email o codice..."
            className="pl-10"
            data-testid="bookings-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="bookings-status-filter">
            <Filter size={16} className="mr-2" />
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="in_attesa">In Attesa</SelectItem>
            <SelectItem value="accettata">Accettate</SelectItem>
            <SelectItem value="rifiutata">Rifiutate</SelectItem>
            <SelectItem value="completata">Completate</SelectItem>
            <SelectItem value="annullata">Annullate</SelectItem>
            <SelectItem value="archiviata">Archiviate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {filteredBookings.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded">
          <Checkbox
            checked={selectedIds.length === filteredBookings.length && filteredBookings.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-bookings"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.length > 0 ? `${selectedIds.length} selezionate` : 'Seleziona tutto'}
          </span>
          
          {selectedIds.length > 0 && (
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkConfirm('archive')}
                disabled={bulkLoading}
              >
                <Archive size={14} className="mr-1" />
                Archivia ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowBulkConfirm('delete')}
                disabled={bulkLoading}
              >
                <Trash2 size={14} className="mr-1" />
                Elimina ({selectedIds.length})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk Confirm Modal */}
      <Dialog open={!!showBulkConfirm} onOpenChange={() => setShowBulkConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showBulkConfirm === 'delete' ? 'Conferma Eliminazione' : 'Conferma Archiviazione'}
            </DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Sei sicuro di voler {showBulkConfirm === 'delete' ? 'eliminare' : 'archiviare'}{' '}
            <strong>{selectedIds.length}</strong> prenotazioni?
            {showBulkConfirm === 'delete' && (
              <span className="block text-destructive text-sm mt-2">Questa azione non può essere annullata.</span>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(null)}>Annulla</Button>
            <Button
              variant={showBulkConfirm === 'delete' ? 'destructive' : 'default'}
              onClick={showBulkConfirm === 'delete' ? bulkDelete : bulkArchive}
              disabled={bulkLoading}
            >
              {bulkLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
              {showBulkConfirm === 'delete' ? 'Elimina' : 'Archivia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bookings Table */}
      <div className="bg-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 w-10"></th>
                <th className="text-left p-4 text-sm font-medium">Codice</th>
                <th className="text-left p-4 text-sm font-medium">Cliente</th>
                <th className="text-left p-4 text-sm font-medium">Servizio</th>
                <th className="text-left p-4 text-sm font-medium">Data</th>
                <th className="text-left p-4 text-sm font-medium">Stato</th>
                <th className="text-left p-4 text-sm font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr
                  key={booking.id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                  data-testid={`booking-row-${booking.id}`}
                >
                  <td className="p-4">
                    <Checkbox
                      checked={selectedIds.includes(booking.id)}
                      onCheckedChange={() => toggleSelect(booking.id)}
                    />
                  </td>
                  <td className="p-4 font-mono text-sm">{booking.booking_code}</td>
                  <td className="p-4">
                    <div className="font-medium">{booking.first_name} {booking.last_name}</div>
                    <div className="text-sm text-muted-foreground">{booking.email}</div>
                  </td>
                  <td className="p-4">{booking.service_type}</td>
                  <td className="p-4">
                    <div>{booking.preferred_date}</div>
                    {booking.time_slot && (
                      <div className="text-sm text-muted-foreground">{booking.time_slot}</div>
                    )}
                  </td>
                  <td className="p-4">{getStatusBadge(booking.status)}</td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(booking)}
                        data-testid={`view-booking-${booking.id}`}
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archiveBooking(booking.id)}
                        title="Archivia"
                      >
                        <Archive size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteBooking(booking.id)}
                        title="Elimina"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBookings.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            Nessuna prenotazione trovata
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettagli Prenotazione</DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-lg">{selectedBooking.booking_code}</span>
                {getStatusBadge(selectedBooking.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Cliente</div>
                  <div className="font-medium">{selectedBooking.first_name} {selectedBooking.last_name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Servizio</div>
                  <div className="font-medium">{selectedBooking.service_type}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Data</div>
                  {editMode ? (
                    <Input
                      type="date"
                      value={editData.preferred_date}
                      onChange={(e) => setEditData(d => ({ ...d, preferred_date: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-booking-date"
                    />
                  ) : (
                    <div className="font-medium">{selectedBooking.preferred_date}</div>
                  )}
                </div>
                <div>
                  <div className="text-muted-foreground">Orario</div>
                  {editMode ? (
                    <Input
                      type="text"
                      value={editData.time_slot}
                      onChange={(e) => setEditData(d => ({ ...d, time_slot: e.target.value }))}
                      placeholder="Es: 10:00 o full_day"
                      className="mt-1"
                      data-testid="edit-booking-time"
                    />
                  ) : (
                    <div className="font-medium">{selectedBooking.time_slot || 'Giornata intera'}</div>
                  )}
                </div>
                <div>
                  <div className="text-muted-foreground">Località</div>
                  <div className="font-medium">{selectedBooking.location}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Budget</div>
                  <div className="font-medium">{selectedBooking.budget || 'N/A'}</div>
                </div>
              </div>

              {/* Edit Mode Toggle - Admin can always edit */}
              <div className="flex gap-2">
                {!editMode ? (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-booking-btn">
                    <RefreshCw size={14} className="mr-1" />
                    Modifica Data/Orario
                  </Button>
                ) : (
                  <>
                    <Button size="sm" onClick={saveBookingChanges} disabled={updating} data-testid="save-booking-btn">
                      {updating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
                      Salva Modifiche
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                      Annulla
                    </Button>
                  </>
                )}
              </div>

              <div className="flex gap-4 text-sm">
                <a href={`mailto:${selectedBooking.email}`} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Mail size={14} />
                  {selectedBooking.email}
                </a>
                <a href={`tel:${selectedBooking.phone}`} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Phone size={14} />
                  {selectedBooking.phone}
                </a>
              </div>

              {selectedBooking.notes && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Note cliente</div>
                  <div className="bg-muted/50 p-3 text-sm">{selectedBooking.notes}</div>
                </div>
              )}

              {selectedBooking.voucher_code && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="booking-voucher-info">
                  <span className="font-medium text-emerald-800">Buono regalo {selectedBooking.voucher_code}</span>
                  <span className="text-emerald-700"> · € {Number(selectedBooking.voucher_amount).toFixed(0)} da scalare dal totale</span>
                </div>
              )}

              {selectedBooking.due_date && (
                <div className="bg-[#f6f1ec] border border-[#e5ddd5] p-3 text-sm" data-testid="booking-due-date-info">
                  <span className="font-medium">Data prevista parto:</span> {new Date(selectedBooking.due_date).toLocaleDateString('it-IT')}
                  <span className="text-muted-foreground"> · promemoria newborn {selectedBooking.newborn_reminder_sent_at ? `inviato il ${new Date(selectedBooking.newborn_reminder_sent_at).toLocaleDateString('it-IT')}` : 'programmato 3 settimane prima'}</span>
                </div>
              )}

              {/* Price and Accept Section - 2 SEPARATE STEPS */}
              <div className="p-4 border rounded space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <CreditCard size={16} />
                  Prezzo e Accettazione
                </h4>
                
                {/* Budget Display */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Budget indicato (cliente)</div>
                    <div className="font-medium">{selectedBooking.budget || 'Non specificato'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Stato</div>
                    {getStatusBadge(selectedBooking.status)}
                  </div>
                </div>

                {/* STEP 1: Set Price (visible if not yet accepted) */}
                {selectedBooking.status !== 'accettata' && selectedBooking.status !== 'annullata' && selectedBooking.status !== 'rifiutata' && (
                  <div className="pt-3 border-t space-y-3">
                    <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                      <strong>Step 1:</strong> Imposta il prezzo definitivo
                    </div>
                    <div className="space-y-2">
                      <Label>Prezzo Definitivo (€)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Es: 1500.00"
                          value={editData.confirmed_price || selectedBooking.confirmed_price || ''}
                          onChange={(e) => setEditData(d => ({ ...d, confirmed_price: e.target.value }))}
                          data-testid="confirmed-price-input"
                        />
                        <Button 
                          variant="outline"
                          onClick={savePrice} 
                          disabled={updating || !editData.confirmed_price} 
                          data-testid="save-price-btn"
                        >
                          {updating ? <Loader2 size={14} className="animate-spin" /> : 'Salva Prezzo'}
                        </Button>
                      </div>
                      {selectedBooking.confirmed_price > 0 && (
                        <p className="text-sm text-green-600">
                          ✓ Prezzo impostato: €{selectedBooking.confirmed_price.toFixed(2)}
                        </p>
                      )}
                    </div>

                    {/* STEP 2: Accept Request */}
                    <div className="pt-3 border-t">
                      <div className="bg-green-50 p-3 rounded text-sm text-green-800 mb-3">
                        <strong>Step 2:</strong> Accetta la richiesta (abilita pagamento caparra)
                      </div>
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={acceptBooking}
                        disabled={updating || !selectedBooking.confirmed_price || selectedBooking.confirmed_price <= 0}
                        data-testid="accept-booking-btn"
                      >
                        {updating ? (
                          <Loader2 size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Check size={16} className="mr-2" />
                        )}
                        ACCETTA RICHIESTA
                        {selectedBooking.confirmed_price > 0 && (
                          <span className="ml-2 text-xs opacity-80">
                            (Caparra: €{(selectedBooking.confirmed_price * 0.15).toFixed(2)})
                          </span>
                        )}
                      </Button>
                      {(!selectedBooking.confirmed_price || selectedBooking.confirmed_price <= 0) && (
                        <p className="text-xs text-orange-600 mt-2">
                          ⚠ Devi prima impostare il prezzo definitivo
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Show status for ACCEPTED bookings */}
                {selectedBooking.status === 'accettata' && (
                  <div className="pt-3 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-muted-foreground">Prezzo Definitivo</div>
                        <div className="font-medium text-lg text-green-600">€{selectedBooking.confirmed_price?.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Caparra (15%)</div>
                        <div className="font-medium text-lg">€{selectedBooking.deposit_amount?.toFixed(2)}</div>
                      </div>
                      <div>
                        {getPaymentBadge(selectedBooking.payment_status)}
                      </div>
                    </div>
                    <div className="bg-green-50 p-3 rounded text-sm text-green-700">
                      ✓ Richiesta accettata - Il cliente può procedere al pagamento della caparra
                    </div>
                    
                    {/* Stripe Payment Info */}
                    {(selectedBooking.stripe_session_id || selectedBooking.stripe_payment_intent_id || selectedBooking.refund_id) && (
                      <div className="mt-3 p-3 bg-gray-50 rounded text-xs space-y-1">
                        <div className="font-medium text-gray-700">Info Stripe:</div>
                        {selectedBooking.stripe_session_id && (
                          <div className="text-gray-600">Session: {selectedBooking.stripe_session_id.substring(0, 30)}...</div>
                        )}
                        {selectedBooking.stripe_payment_intent_id && (
                          <div className="text-gray-600">Payment Intent: {selectedBooking.stripe_payment_intent_id}</div>
                        )}
                        {selectedBooking.refund_id && (
                          <div className="text-green-600">Refund ID: {selectedBooking.refund_id}</div>
                        )}
                        {selectedBooking.refund_status && (
                          <div className="text-green-600">Refund Status: {selectedBooking.refund_status}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cancellation Request - 3 Clear Action Buttons */}
              {selectedBooking.cancellation_request && (
                <div className="p-4 bg-red-50 border border-red-200 rounded">
                  <h4 className="font-medium text-red-700 mb-2">Richiesta Cancellazione</h4>
                  <p className="text-sm text-red-600 mb-3">
                    <strong>Motivo:</strong> {selectedBooking.cancellation_request.reason}
                  </p>
                  <p className="text-xs text-red-500 mb-4">
                    Richiesta il: {new Date(selectedBooking.cancellation_request.requested_at).toLocaleString('it-IT')}
                  </p>
                  
                  {/* Show deposit info if paid */}
                  {selectedBooking.payment_status === 'caparra_pagata' && (
                    <div className="bg-white p-3 rounded border border-red-200 mb-4 text-sm">
                      <strong>Caparra pagata:</strong> €{selectedBooking.deposit_amount?.toFixed(2)}
                      {selectedBooking.stripe_payment_intent_id && (
                        <div className="text-xs text-gray-500 mt-1">
                          Payment Intent: {selectedBooking.stripe_payment_intent_id}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 3 Clear Action Buttons */}
                  <div className="flex flex-col gap-3">
                    {/* Button 1: Approve + Refund */}
                    <Button 
                      className="w-full justify-start bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => approveCancellation(true)}
                      disabled={updating}
                      data-testid="approve-refund-btn"
                    >
                      {updating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
                      <div className="text-left">
                        <div className="font-medium">Approva + Rimborsa Caparra</div>
                        {selectedBooking.payment_status === 'caparra_pagata' && (
                          <div className="text-xs opacity-80">Rimborso €{selectedBooking.deposit_amount?.toFixed(2)} via Stripe</div>
                        )}
                        {selectedBooking.payment_status !== 'caparra_pagata' && (
                          <div className="text-xs opacity-80">Nessuna caparra da rimborsare</div>
                        )}
                      </div>
                    </Button>
                    
                    {/* Button 2: Approve + Keep Deposit */}
                    <Button 
                      className="w-full justify-start bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => approveCancellation(false)}
                      disabled={updating}
                      data-testid="approve-no-refund-btn"
                    >
                      {updating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
                      <div className="text-left">
                        <div className="font-medium">Approva + Trattieni Caparra</div>
                        {selectedBooking.payment_status === 'caparra_pagata' && (
                          <div className="text-xs opacity-80">Caparra €{selectedBooking.deposit_amount?.toFixed(2)} trattenuta</div>
                        )}
                        {selectedBooking.payment_status !== 'caparra_pagata' && (
                          <div className="text-xs opacity-80">Nessuna caparra versata</div>
                        )}
                      </div>
                    </Button>
                    
                    {/* Button 3: Reject Cancellation */}
                    <Button 
                      variant="outline"
                      className="w-full justify-start border-gray-300"
                      onClick={() => updateStatus('confermata')}
                      disabled={updating}
                      data-testid="reject-cancellation-btn"
                    >
                      <X size={16} className="mr-2" />
                      <div className="text-left">
                        <div className="font-medium">Rifiuta Cancellazione</div>
                        <div className="text-xs text-muted-foreground">Prenotazione resta attiva</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Modification Request */}
              {selectedBooking.modification_request && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="font-medium text-blue-700 mb-2">Richiesta Modifica</h4>
                  {selectedBooking.modification_request.new_date && (
                    <p className="text-sm text-blue-600">
                      Nuova data: <strong>{selectedBooking.modification_request.new_date}</strong>
                    </p>
                  )}
                  {selectedBooking.modification_request.new_time_slot && (
                    <p className="text-sm text-blue-600">
                      Nuovo orario: <strong>{selectedBooking.modification_request.new_time_slot}</strong>
                    </p>
                  )}
                  {selectedBooking.modification_request.reason && (
                    <p className="text-sm text-blue-600 mt-1">
                      Motivo: {selectedBooking.modification_request.reason}
                    </p>
                  )}
                  <p className="text-xs text-blue-500 mt-2 mb-3">
                    Richiesta il: {new Date(selectedBooking.modification_request.requested_at).toLocaleString('it-IT')}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={approveModification} disabled={updating}>
                      {updating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                      Approva Modifica
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus('confermata')} disabled={updating}>
                      Rifiuta Richiesta
                    </Button>
                  </div>
                </div>
              )}

              {/* CANCELLED BOOKING - Refund Status Summary */}
              {selectedBooking.status === 'annullata' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded" data-testid="cancellation-summary">
                  <h4 className="font-medium text-gray-700 mb-3">Riepilogo Cancellazione</h4>
                  
                  {/* Refund Decision & Status */}
                  <div className="space-y-2 text-sm">
                    {selectedBooking.deposit_amount > 0 && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Caparra:</span>
                        <span className="font-medium">€{selectedBooking.deposit_amount?.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Refund Decision */}
                    {selectedBooking.refund_decision && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Decisione Admin:</span>
                        <span className={`font-medium ${selectedBooking.refund_decision === 'refund' ? 'text-green-600' : 'text-orange-600'}`}>
                          {selectedBooking.refund_decision === 'refund' ? 'Rimborsa' : 'Trattieni'}
                        </span>
                      </div>
                    )}
                    
                    {/* Refund Status with clear messaging */}
                    {selectedBooking.refund_status && selectedBooking.refund_status !== 'not_applicable' && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Stato Rimborso:</span>
                        <span className={`font-medium px-2 py-0.5 rounded text-xs ${
                          selectedBooking.refund_status === 'succeeded' ? 'bg-green-100 text-green-700' :
                          selectedBooking.refund_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          selectedBooking.refund_status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {selectedBooking.refund_status === 'succeeded' ? '✓ Caparra Rimborsata' :
                           selectedBooking.refund_status === 'pending' ? '⏳ Rimborso in corso' :
                           selectedBooking.refund_status === 'failed' ? '✗ Rimborso fallito' :
                           selectedBooking.refund_status}
                        </span>
                      </div>
                    )}
                    
                    {/* Legacy support: show old refund_authorized if new fields not present */}
                    {!selectedBooking.refund_decision && selectedBooking.refund_authorized !== null && selectedBooking.refund_authorized !== undefined && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Rimborso:</span>
                        <span className={`font-medium ${selectedBooking.refund_authorized ? 'text-green-600' : 'text-orange-600'}`}>
                          {selectedBooking.refund_authorized ? 'Autorizzato' : 'Non autorizzato (caparra trattenuta)'}
                        </span>
                      </div>
                    )}
                    
                    {/* Stripe Info */}
                    {selectedBooking.stripe_payment_intent_id && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Payment Intent:</span>
                        <span className="font-mono text-xs">{selectedBooking.stripe_payment_intent_id}</span>
                      </div>
                    )}
                    
                    {selectedBooking.refund_id && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Refund ID:</span>
                        <span className="font-mono text-xs text-green-600">{selectedBooking.refund_id}</span>
                      </div>
                    )}
                    
                    {selectedBooking.refunded_at && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Data Rimborso:</span>
                        <span className="text-sm">{new Date(selectedBooking.refunded_at).toLocaleString('it-IT')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="mb-2 block">Note Admin</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Note interne..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {/* Admin can ALWAYS modify bookings - regardless of status */}
            {!selectedBooking?.cancellation_request && !selectedBooking?.modification_request && (
              <div className="flex flex-wrap gap-2 w-full justify-end">
                {/* Quick actions for new/pending bookings */}
                {/* Quick Actions for In Attesa bookings */}
                {selectedBooking?.status === 'in_attesa' && (
                  <Button
                    variant="outline"
                    onClick={() => updateStatus('rifiutata')}
                    disabled={updating}
                    className="text-destructive"
                    data-testid="reject-booking-btn"
                  >
                    <X size={16} className="mr-1" />
                    Rifiuta
                  </Button>
                )}
                
                {/* Complete Event - only for accepted bookings with paid deposit */}
                {selectedBooking?.status === 'accettata' && selectedBooking?.payment_status === 'caparra_pagata' && (
                  <Button
                    variant="outline"
                    onClick={() => updateStatus('completata')}
                    disabled={updating}
                    className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                    data-testid="complete-booking-btn"
                  >
                    {updating ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Check size={16} className="mr-1" />
                    )}
                    Completa Evento
                  </Button>
                )}
                
                {/* Status selector - limited options based on current state */}
                <Select
                  value={selectedBooking?.status}
                  onValueChange={(value) => updateStatus(value)}
                  disabled={updating}
                >
                  <SelectTrigger className="w-48" data-testid="booking-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                    <SelectItem value="accettata" disabled={!selectedBooking?.confirmed_price}>
                      Accettata {!selectedBooking?.confirmed_price && '(imposta prezzo)'}
                    </SelectItem>
                    <SelectItem value="rifiutata">Rifiutata</SelectItem>
                    <SelectItem value="completata" disabled={selectedBooking?.status !== 'accettata'}>
                      Completata
                    </SelectItem>
                    <SelectItem value="annullata">Annullata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
